import { useEffect, useState } from "react";
import { toast } from "sonner";
import MainLayout from "screens/partials/layout";
import PageHeader from "components/page-header";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Switch } from "components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { CURRENCY } from "helpers/constants";
import { FeeRequests, type CheckoutFee } from "queries/fee-queries";

const money = (amount: string) =>
  Number(amount).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function SettingsCheckoutFees() {
  const [fees, setFees] = useState<CheckoutFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editAmount, setEditAmount] = useState("");

  useEffect(() => {
    FeeRequests.list()
      .then(setFees)
      .catch((error) => toast.error(error.message))
      .finally(() => setLoading(false));
  }, []);

  const replace = (updated: CheckoutFee) =>
    setFees((current) => current.map((fee) => (fee.id === updated.id ? updated : fee)));

  const add = async () => {
    setSaving(true);
    try {
      const created = await FeeRequests.create(title, amount);
      setFees((current) => [...current, created]);
      setTitle("");
      setAmount("");
      toast.success("Fee added. Customers will see it at checkout.");
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (fee: CheckoutFee, active: boolean) => {
    try {
      replace(await FeeRequests.update(fee.id, { is_active: active }));
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const saveEdit = async (fee: CheckoutFee) => {
    try {
      replace(await FeeRequests.update(fee.id, { title: editTitle, amount: editAmount }));
      setEditingId(null);
      toast.success("Fee updated");
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const remove = async (fee: CheckoutFee) => {
    if (!window.confirm(`Delete "${fee.title}"? Customers will no longer be charged it.`)) return;
    try {
      await FeeRequests.remove(fee.id);
      setFees((current) => current.filter((item) => item.id !== fee.id));
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  return (
    <MainLayout>
      <div className="w-full">
        <PageHeader
          eyebrow="Settings"
          title="Checkout Fees"
          description="Fixed charges added to every order, such as takeaway or packaging. Customers see them at checkout and can't remove them. They are part of what you are paid for the order."
        />

        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-8 space-y-5">
            <Card>
              <CardHeader className="border-b px-6 py-5 mb-6">
                <CardTitle className="text-lg">Add a fee</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-3">
                  <Input
                    className="flex-1 min-w-[180px]"
                    placeholder="Title, e.g. Takeaway"
                    value={title}
                    maxLength={100}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                  <Input
                    className="w-40"
                    type="number"
                    min={0}
                    placeholder={`Amount (${CURRENCY})`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  <Button
                    type="button"
                    onClick={add}
                    disabled={saving || !title.trim() || !amount.trim()}
                  >
                    Add fee
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="border-b px-6 py-5 mb-6">
                <CardTitle className="text-lg">Your fees</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-sm text-default-500">Loading...</p>
                ) : fees.length === 0 ? (
                  <p className="text-sm text-default-500">
                    No fees yet. Add one above and it will appear on every order.
                  </p>
                ) : (
                  <ul className="divide-y divide-default-200">
                    {fees.map((fee) => (
                      <li key={fee.id} className="flex flex-wrap items-center gap-3 py-3">
                        {editingId === fee.id ? (
                          <>
                            <Input
                              className="flex-1 min-w-[160px]"
                              value={editTitle}
                              maxLength={100}
                              onChange={(e) => setEditTitle(e.target.value)}
                            />
                            <Input
                              className="w-36"
                              type="number"
                              min={0}
                              value={editAmount}
                              onChange={(e) => setEditAmount(e.target.value)}
                            />
                            <Button type="button" size="sm" onClick={() => saveEdit(fee)}>
                              Save
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingId(null)}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <div className="flex-1 min-w-[160px]">
                              <div
                                className={
                                  fee.is_active
                                    ? "font-medium text-default-900"
                                    : "font-medium text-default-400 line-through"
                                }
                              >
                                {fee.title}
                              </div>
                              <div className="text-sm text-default-500">
                                {CURRENCY}
                                {money(fee.amount)}
                                {!fee.is_active && " · hidden from customers"}
                              </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm text-default-600">
                              <Switch
                                checked={fee.is_active}
                                onCheckedChange={(checked) => toggle(fee, checked)}
                              />
                              Active
                            </label>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingId(fee.id);
                                setEditTitle(fee.title);
                                setEditAmount(String(Number(fee.amount)));
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => remove(fee)}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
